export interface Folder {
  id: string;
  name: string;
  createdAt: string;
  fileCount?: number;
  totalSize?: number;
  transcribedCount?: number;
}

export interface ProcessingEstimation {
  elapsedSeconds: number;
  estimatedTotalSeconds: number;
}

export interface AudioFile {
  id: string;
  name: string;
  filename: string;
  fileSize: number;
  durationSeconds?: number;
  contentType: string;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  folderId?: string | null;
  comment?: string;
  durationDisplay?: string;
  fileSizeDisplay: string;
  folderName?: string;
  hasTranscript: boolean;
  transcriptId?: string;
  processingEstimation?: ProcessingEstimation | null;
}

export interface FileStatusUpdate {
  type: 'file_status_changed';
  file_id: string;
  status: 'processing' | 'ready' | 'failed';
  message: string;
  file: {
    id: string;
    name: string;
    status: 'processing' | 'ready' | 'failed';
    has_transcript: boolean;
    updated_at: string;
  };
}

export interface ExportCompletedUpdate {
  type: 'export_completed';
  export_id: string;
  message: string;
  download_url: string;
  filename: string;
  format: string;
  expires_at: string;
}

export type WebSocketMessage = FileStatusUpdate | ExportCompletedUpdate;

export interface TranscriptWord {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  position: number;
  confidenceScore: number;
  hasEntities: boolean;
}

export interface EditorWord extends TranscriptWord {
  isNew?: boolean;
  isModified?: boolean;
  lastModifiedAt?: number;
  originalConfidenceScore?: number; // Store original confidence for cycling
}

export interface TranscriptUtterance {
  id: string;
  startTime: number;
  endTime: number;
  position: number;
  confidenceScore: number;
  sentiment?: string;
  sentimentConfidence?: number;
  speakerId: string;
  transcriptWords: EditorWord[];
}

// Helper function to convert words array to plain text
export const wordsToPlainText = (transcriptWords: TranscriptWord[]): string => {
  return transcriptWords.map((w) => w.text).join(' ');
};

// Utility function to get display text from utterance
export const getUtteranceDisplayText = (
  utterance: TranscriptUtterance
): string => {
  return utterance.transcriptWords.map((w) => w.text).join(' ');
};

export interface Speaker {
  id: string;
  originalLabel: string;
  name: string;
  role?: string;
  displayName: string;
  utteranceCount: number;
  speakingTimeSeconds: number;
  speakingPercentage: number;
  identified: boolean;
  createdAt: string;
}

export interface Transcript {
  id: string;
  transcriptionService: string;
  startedAt: string;
  completedAt: string;
  wordCount: number;
  confidenceScore: number;
  notes?: string;
  createdAt: string;
  duration: number;
  speakerCount: number;
  accuracyPercentage: number;
  processingTime?: number;
  speakers: Speaker[];
  summary?: string;
  summaryModel?: string;
  summaryType?: string;
}

export interface Export {
  id: string;
  format: 'pdf' | 'docx';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_url?: string;
  filename: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  processing_time?: number;
  expired: boolean;
  expires_at: string;
  audio_file_id: string;
  audio_filename: string;
}

export interface TranscriptionService {
  key: string;
  name: string;
  supported_formats: string[];
  available: boolean;
  error_message?: string;
}

export interface ServiceConfiguration {
  transcription: {
    active_service: string;
    active_service_name: string;
    supported_formats: string[];
    max_file_size: number;
    all_services: TranscriptionService[];
  };
  file_constraints: {
    max_file_size: number;
    supported_formats: string[];
  };
}

export interface UserPreferences {
  theme?: 'light' | 'dark';
  transcription_service?: string;
  transcript?: {
    utterance_meta_column?: boolean;
  };
  sidebar?: {
    files_collapsed?: boolean;
    tools_collapsed?: boolean;
  };
  tutorials?: {
    shown?: string[];
    skip_all?: boolean;
  };
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  provider: string | null;
  created_at: string;
  avatar_url: string | null;
  has_password: boolean;
  preferences: UserPreferences;
  profile_completed: boolean;
  confirmed_email_at: string | null;
}

type EditHistoryOperationType =
  | 'notes_edited'
  | 'summary_edited'
  | 'speaker_created'
  | 'speaker_deleted'
  | 'speaker_edited'
  | 'utterance_created'
  | 'utterance_deleted'
  | 'utterance_edited'
  | 'utterance_speaker_changed'
  | 'utterance_words_edited'
  | 'utterance_split'
  | 'utterance_merged';

export interface EditHistoryOperation {
  type: EditHistoryOperationType;
  timestamp: string;
}

interface EditHistoryEntry extends EditHistoryOperation {
  index: number;
  isCurrent: boolean;
  canUndoTo: boolean;
  summary: string;
}

export interface EditHistory {
  canUndo: boolean;
  canRedo: boolean;
  currentIndex: number;
  operations: EditHistoryEntry[];
}

export interface EditHistoryHook {
  editHistory: EditHistory | null;
  isLoading: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  refreshHistory: () => void;
}

export type OperationType =
  | 'utterance_speaker_change'
  | 'utterance_words_edit'
  | 'utterance_split'
  | 'utterance_merge';

interface BaseOperation {
  utteranceId: string;
  sentAt?: number;
}

export type SyncOperation =
  | (BaseOperation & {
      type: 'utterance_speaker_change';
      data: {
        newSpeakerId: string;
      };
    })
  | (BaseOperation & {
      type: 'utterance_words_edit';
      data: {
        newEditorWords: EditorWord[];
      };
    })
  | (BaseOperation & {
      type: 'utterance_split';
      data: {
        newUtteranceId: string;
        firstWordId: string;
      };
    })
  | (BaseOperation & {
      type: 'utterance_merge';
      data: {
        targetUtteranceId: string;
      };
    });

export type SyncStatus = 'synced' | 'pending' | 'error';
